import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import FamilyWizardLayout from '../FamilyWizardLayout'
import { SubmitButton } from '../student_details/shared/submit-button'
import { useEffect, useRef, useState } from 'react'
import { SkipVerification } from './skip-verification/SkipVerification'
import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '../constants'

const FORM_ID = 'income-verification-form'

const verificationSchema = z.object({
  verificationType: z.enum(['tax-commission', 'manual-upload', 'opt-out'], {
    required_error: 'Please select a verification method',
  }),
})

type VerificationSchema = z.infer<typeof verificationSchema>

export function IncomeVerificationType() {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const [skip, setSkip] = useState(false)

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Income Verification Type page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationSchema>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      verificationType: undefined,
    },
  })

  const onSubmit = (data: VerificationSchema) => {
    switch (data.verificationType) {
      case 'tax-commission':
        navigate(
          AppRoutePaths.family_application.income_verification.tax_commission
        )
        break
      case 'manual-upload':
        navigate(
          AppRoutePaths.family_application.income_verification
            .document_instructions
        )
        break
      case 'opt-out':
        setSkip(true)
        break
    }
  }

  if (skip) {
    return <SkipVerification onCancel={() => setSkip(false)} />
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('income_verification_type')}</h1>
      </FamilyWizardLayout.Header>

      <FamilyWizardLayout.Content>
        <section id="income-verification">
          <p className="text-gray-600 mb-6 text-center">
            {translate('income_verification_description')}
          </p>

          <h3 className="text-lg font-medium text-left mb-2 mt-4">
            {translate('please_select_income_type')}
          </h3>
          <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="verificationType"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="space-y-4"
                >
                  <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <RadioGroupItem
                      value="tax-commission"
                      id="tax-commission"
                    />
                    <div className="flex-grow">
                      <Label
                        htmlFor="tax-commission"
                        className="font-medium block mb-1"
                      >
                        {translate('utah_tax_commission')}
                      </Label>
                      <p className="text-sm text-gray-600">
                        {translate('utah_tax_commission_text')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 ">
                    <RadioGroupItem value="opt-out" id="opt-out" />
                    <div className="flex-grow">
                      <Label
                        htmlFor="opt-out"
                        className="font-medium block mb-1"
                      >
                        {translate('dont_verify')}
                      </Label>
                      <div className="flex items-start space-x-2 mt-1 p-2  rounded">
                        <p className="text-sm text-gray-600">
                          {translate('dont_verify_text')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 ">
                    <RadioGroupItem value="manual-upload" id="manual-upload" />
                    <div className="flex-grow">
                      <Label
                        htmlFor="manual-upload"
                        className="font-medium block mb-1"
                      >
                        {translate('manual_upload')}
                      </Label>
                      <p className="text-sm text-gray-600">
                        {translate('manual_upload_text')}
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.verificationType && (
              <p className="text-red-500 text-sm mt-2">
                {errors.verificationType.message}
              </p>
            )}
          </form>
        </section>
      </FamilyWizardLayout.Content>

      <FamilyWizardLayout.ActionBar className="flex flex-col gap-2">
        <SubmitButton
          buttonTxt={translate('continue')}
          form={FORM_ID}
          status="idle"
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
